const DEFAULT_BOOSTERS = Object.freeze({ magnet: 0, hammer: 0, shuffle: 0 });

function clonePlain(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeBoosterCounts(level) {
  const counts = { ...DEFAULT_BOOSTERS };
  for (const booster of level.boosters || []) {
    if (!booster?.id) continue;
    counts[booster.id] = Math.max(0, Number(booster.count) || 0);
  }
  return counts;
}

function normalizeBins(level) {
  const bins = {};
  for (const target of level.storageTargets || []) {
    bins[target.color] = {
      count: 0,
      capacity: Math.max(0, Number(target.capacity) || 0),
    };
  }
  return bins;
}

function interactiveBolts(level) {
  return (level.bolts || []).filter((bolt) => bolt.interactive !== false);
}

function requiredBoltIds(level) {
  return Array.isArray(level.winCondition?.requiredBoltIds)
    ? [...level.winCondition.requiredBoltIds]
    : interactiveBolts(level).map((bolt) => bolt.id);
}

function visibleInitially(bolt) {
  return bolt.interactive === false || bolt.hidden !== true;
}

function releaseIds(level, removedIds) {
  return (level.objects || [])
    .filter((object) => Array.isArray(object.releaseBoltIds)
      && object.releaseBoltIds.length > 0
      && object.releaseBoltIds.every((id) => removedIds.has(id)))
    .map((object) => object.id);
}

function revealIds(level, removedIds, alreadyVisible) {
  return interactiveBolts(level)
    .filter((bolt) => bolt.hidden === true && !alreadyVisible.has(bolt.id))
    .filter((bolt) => {
      const dependencies = Array.isArray(bolt.revealAfterBoltIds) ? bolt.revealAfterBoltIds : [];
      return dependencies.length > 0 && dependencies.every((id) => removedIds.has(id));
    })
    .map((bolt) => bolt.id);
}

export function createGameSession(level, { rng = Math.random } = {}) {
  if (!level || typeof level !== 'object') throw new TypeError('A level object is required.');

  const boltById = new Map((level.bolts || []).map((bolt) => [bolt.id, bolt]));
  const required = new Set(requiredBoltIds(level));
  const removed = new Set();
  const visible = new Set((level.bolts || []).filter(visibleInitially).map((bolt) => bolt.id));
  const releasedObjects = new Set();
  const colors = new Map((level.bolts || []).map((bolt) => [bolt.id, bolt.color]));
  const bins = normalizeBins(level);
  const boosters = normalizeBoosterCounts(level);

  let movesRemaining = Math.max(0, Number(level.moveLimit) || 0);
  let status = 'playing';
  let mode = null;
  let paused = false;
  let actions = 0;

  function snapshot() {
    return {
      levelId: level.id,
      displayNumber: level.displayNumber,
      movesRemaining,
      moveLimit: Number(level.moveLimit) || 0,
      status,
      mode,
      paused,
      actions,
      removedBoltIds: [...removed],
      visibleBoltIds: [...visible],
      releasedObjectIds: [...releasedObjects],
      bins: clonePlain(bins),
      boosters: { ...boosters },
      currentColors: Object.fromEntries(colors),
      remainingRequiredBoltIds: [...required].filter((id) => !removed.has(id)),
    };
  }

  function isBoltVisible(id) {
    return visible.has(id);
  }

  function isBoltRemoved(id) {
    return removed.has(id);
  }

  function canAct() {
    return status === 'playing' && !paused;
  }

  function canRemoveBolt(id) {
    const bolt = boltById.get(id);
    return Boolean(
      canAct()
      && bolt
      && bolt.interactive !== false
      && visible.has(id)
      && !removed.has(id),
    );
  }

  function refreshDerivedState() {
    const newlyReleased = releaseIds(level, removed)
      .filter((id) => !releasedObjects.has(id));
    newlyReleased.forEach((id) => releasedObjects.add(id));

    const newlyVisible = revealIds(level, removed, visible);
    newlyVisible.forEach((id) => visible.add(id));

    const won = [...required].every((id) => removed.has(id));
    if (won) status = 'won';
    else if (movesRemaining <= 0) status = 'lost';

    return { newlyReleased, newlyVisible, status };
  }

  function removeBolt(id, { consumeMove = true, effect = 'normal' } = {}) {
    if (!canRemoveBolt(id)) {
      return { ok: false, reason: 'bolt-not-removable', state: snapshot() };
    }

    const bolt = boltById.get(id);
    removed.add(id);
    if (consumeMove) movesRemaining = Math.max(0, movesRemaining - 1);
    actions += 1;

    const color = colors.get(id) || bolt.color;
    if (!bins[color]) bins[color] = { count: 0, capacity: 0 };
    bins[color].count += 1;

    const derived = refreshDerivedState();

    return {
      ok: true,
      type: 'bolt-removed',
      boltId: id,
      color,
      effect,
      consumeMove,
      ...derived,
      state: snapshot(),
    };
  }

  function setPaused(value) {
    if (status !== 'playing' && value) return snapshot();
    paused = Boolean(value);
    return snapshot();
  }

  function armBooster(name) {
    if (!canAct() || !Object.hasOwn(boosters, name) || boosters[name] <= 0 || name === 'shuffle') {
      return { ok: false, reason: 'booster-unavailable', state: snapshot() };
    }
    mode = mode === name ? null : name;
    return { ok: true, mode, state: snapshot() };
  }

  function useHammer(id) {
    if (!canAct() || boosters.hammer <= 0) return { ok: false, reason: 'booster-unavailable', state: snapshot() };
    const result = removeBolt(id, { consumeMove: false, effect: 'hammer' });
    if (!result.ok) return result;
    boosters.hammer -= 1;
    mode = null;
    return { ...result, booster: 'hammer', state: snapshot() };
  }

  function remainingRemovableIds(exclude = new Set()) {
    return interactiveBolts(level)
      .map((bolt) => bolt.id)
      .filter((id) => canRemoveBolt(id) && !exclude.has(id));
  }

  function useMagnet(id) {
    if (!canAct() || boosters.magnet <= 0) return { ok: false, reason: 'booster-unavailable', state: snapshot() };
    const first = removeBolt(id, { consumeMove: true, effect: 'magnet' });
    if (!first.ok) return first;

    const candidates = remainingRemovableIds(new Set([id]));
    const secondId = candidates[0] || null;
    let second = null;
    if (secondId && status === 'playing') {
      second = removeBolt(secondId, { consumeMove: false, effect: 'magnet' });
    }

    boosters.magnet -= 1;
    mode = null;
    return {
      ok: true,
      type: 'magnet-used',
      first,
      second,
      removedBoltIds: [id, secondId].filter(Boolean),
      state: snapshot(),
    };
  }

  function shuffleRemainingColors() {
    if (!canAct() || boosters.shuffle <= 0) {
      return { ok: false, reason: 'booster-unavailable', state: snapshot() };
    }

    const ids = remainingRemovableIds();
    const shuffledColors = ids.map((id) => colors.get(id));
    for (let i = shuffledColors.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.max(0, Math.min(0.999999, Number(rng()))) * (i + 1));
      [shuffledColors[i], shuffledColors[j]] = [shuffledColors[j], shuffledColors[i]];
    }
    ids.forEach((id, index) => colors.set(id, shuffledColors[index]));

    boosters.shuffle -= 1;
    mode = null;
    actions += 1;

    return {
      ok: true,
      type: 'shuffle-used',
      assignments: Object.fromEntries(ids.map((id) => [id, colors.get(id)])),
      state: snapshot(),
    };
  }

  function activateBolt(id) {
    if (mode === 'hammer') return useHammer(id);
    if (mode === 'magnet') return useMagnet(id);
    return removeBolt(id, { consumeMove: true, effect: 'normal' });
  }

  return Object.freeze({
    snapshot,
    isBoltVisible,
    isBoltRemoved,
    canRemoveBolt,
    activateBolt,
    removeBolt,
    armBooster,
    useHammer,
    useMagnet,
    shuffleRemainingColors,
    setPaused,
  });
}
